import { normalizeDocumentDataUrl, isPdf } from "../../../services/Functions";

export const METER_ID_KWH = 1;
export const METER_ID_KVARH = 2;
export const METER_ID_MDI = 3;

export const isMdiMeterRow = (row) => {
  if (!row) return false;
  const meterId = Number(row.meterId ?? row.METER_ID);
  return meterId === METER_ID_MDI || Number(row.meterRange) === 0;
};

/**
 * True when consumer actually entered a claimed reading.
 * Form submits `consumerClaimedReading: 0` for blank rows, so 0 without
 * evidence image must not count as a claim.
 */
export const hasConsumerClaimedReading = (row) => {
  if (!row || isMdiMeterRow(row)) return false;
  if (row.imageSrc) return true;
  const claimed = toNumberOrNull(row.claimed);
  return claimed != null && claimed !== 0;
};

/** True when a meter present/to-be reading actually exists (0 = blank in MRC). */
export const hasMrcPresentReading = (value) => {
  const n = toNumberOrNull(value);
  return n != null && n !== 0;
};

export const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Strict true-only parse for isDocumentReuploaded.
 * null / false / "null" / "false" / missing → false.
 */
export const parseMrcIsDocumentReuploaded = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "true" || s === "1";
  }
  return false;
};

export const MAX_READING_DECIMALS = 2;

/** Segregation month count (API: noOfMonths). Default 1. Options 1, 2, 3. */
export const MRC_SEGREGATION_MONTH_LABEL = "No. of Segregate Months";
export const MRC_SEGREGATION_MONTH_OPTIONS = [1, 2, 3];
export const MRC_SEGREGATION_MONTH_DEFAULT = 1;

export const normalizeMrcSegregationMonth = (value) => {
  const n = Number(value);
  return MRC_SEGREGATION_MONTH_OPTIONS.includes(n)
    ? n
    : MRC_SEGREGATION_MONTH_DEFAULT;
};

const MRC_NO_OF_MONTHS_KEYS = new Set([
  "noofmonths",
  "no_of_months",
  "nmonth",
  "n_month",
  "segregationmonth",
  "segregation_month",
  "segregationmonths",
]);

const pickMrcNoOfMonthsRaw = (source) => {
  if (!source || typeof source !== "object") return undefined;
  let fallback;
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined || value === null || value === "") continue;
    const normalizedKey = String(key).replace(/[_-]/g, "").toLowerCase();
    if (normalizedKey === "noofmonths") return value;
    if (
      fallback === undefined &&
      (MRC_NO_OF_MONTHS_KEYS.has(normalizedKey) ||
        MRC_NO_OF_MONTHS_KEYS.has(String(key).toLowerCase()))
    ) {
      fallback = value;
    }
  }
  return fallback;
};

export const pickMrcSegregationMonth = (source) =>
  normalizeMrcSegregationMonth(pickMrcNoOfMonthsRaw(source));

/** Stamp getAllApplications.noOfMonths onto the row used by the workflow. */
export const withMrcNoOfMonths = (row) => {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    noOfMonths: pickMrcSegregationMonth(row),
  };
};

/** Keep digits and at most `maxDecimals` places after the decimal. */
export const sanitizeReadingInput = (
  value,
  maxDecimals = MAX_READING_DECIMALS,
) => {
  const raw = String(value ?? "");
  if (raw === "") return "";
  let cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  const intPart = cleaned.slice(0, dot);
  const decPart = cleaned
    .slice(dot + 1)
    .replace(/\./g, "")
    .slice(0, maxDecimals);
  return `${intPart}.${decPart}`;
};

export const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (n === 0) return "0";
  if (Number.isInteger(n)) return String(n);
  const twoDp = Number(n.toFixed(2));
  if (twoDp !== 0) return String(twoDp);
  return String(Number(n.toFixed(3)));
};

/** Round units to at most 2 decimal places (whole numbers stay whole). */
export const roundUnitsValue = (n) => {
  if (!Number.isFinite(n)) return n;
  return Number(n.toFixed(MAX_READING_DECIMALS));
};

/** Units Rev. / Adj.: decimals allowed up to 2 digits only. */
export const formatUnitsRevisedDisplay = (value, empty = "—") => {
  if (value === null || value === undefined || value === "") return empty;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const rounded = roundUnitsValue(n);
  if (rounded === 0) return "0";
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(MAX_READING_DECIMALS);
};

/** Units Adj. = (reading − As Charged) × MF
 * reading = User Claimed | Site Survey | Authority Approved (stage ke hisaab se)
 * Decimal values are kept to 2 digits.
 */
export const calcUnitsAdjusted = (reading, asCharged, mf) => {
  const r = toNumberOrNull(reading);
  const a = toNumberOrNull(asCharged);
  if (r === null || a === null) return null;
  const m = toNumberOrNull(mf);
  const factor = m === null || m === 0 ? 1 : m;
  return roundUnitsValue((r - a) * factor);
};

/** Display Units Adj. with at most 2 decimal digits. */
export const formatUnitsAdjDisplay = (value, empty = "—") =>
  formatUnitsRevisedDisplay(value, empty);

/** Units Charged = round((As Charged − Previous) × MF) */
export const calcUnitsCharged = (asCharged, previous, mf) => {
  const a = toNumberOrNull(asCharged);
  const p = toNumberOrNull(previous);
  if (a === null || p === null) return null;
  const m = toNumberOrNull(mf);
  const factor = m === null || m === 0 ? 1 : m;
  return Math.round((a - p) * factor);
};

/**
 * Shared checks for an entered To-Be reading (Claimed / Site Survey / Authority).
 * Call only when the user actually typed/selected a value (blank optional = skip).
 */
export const getMrcEnteredReadingError = (
  reading,
  row = {},
  label = "Reading",
) => {
  const n = toNumberOrNull(reading);
  if (n === null) {
    return `${label} must be numeric.`;
  }

  const previous = toNumberOrNull(row.previous ?? row.previousReading);
  if (previous != null && n <= previous) {
    return `${label} must be greater than Previous Reading.`;
  }

  const asCharged = row.asCharged ?? row.asChargedReading;
  const unitsAdjusted = calcUnitsAdjusted(n, asCharged, row.mf);
  if (unitsAdjusted !== null && unitsAdjusted === 0) {
    return "Units Adj. cannot be 0.";
  }

  return "";
};

/** Units Charged display: always a whole number. */
export const formatUnitsChargedDisplay = (value, empty = "—") => {
  if (value === null || value === undefined || value === "") return empty;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(Math.round(n));
};

/** Units Revised = (reading − Previous) × MF
 * Form: Claimed; Site Survey: Survey; Authority: Authority Approved.
 */
export const calcUnitsChargedRevised = (claimed, previous, mf) => {
  const c = toNumberOrNull(claimed);
  const p = toNumberOrNull(previous);
  if (c === null || p === null) return null;
  const m = toNumberOrNull(mf);
  const factor = m === null || m === 0 ? 1 : m;
  return roundUnitsValue((c - p) * factor);
};

/** Net meter KWH sets: 1 Import Off-Peak, 2 Import Peak, 3 Export Off-Peak, 4 Export Peak */
export const NET_METER_KWH_LABEL_BY_SET = {
  1: "Import Off-Peak KWH",
  2: "Import Peak KWH",
  3: "Export Off-Peak KWH",
  4: "Export Peak KWH",
};

export const netMeterDirectionFromSet = (setNo) => {
  const n = Number(setNo);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n >= 3 ? "Export" : "Import";
};

const joinMeterLabel = (...parts) =>
  parts.filter((part) => part != null && String(part).trim() !== "").join(" ");

/** Peak / Off-Peak from meter code (TOU). */
const peakOffPeakFromMeterCode = (code) => {
  const c = String(code ?? "").trim().toLowerCase();
  if (!c) return null;
  if (
    c === "050600" ||
    c.endsWith("0600") ||
    c.endsWith("0506") ||
    c.includes("peak") && !c.includes("off")
  ) {
    return "Peak";
  }
  if (
    c === "050500" ||
    c.endsWith("0500") ||
    c.endsWith("0505") ||
    c.includes("off")
  ) {
    return "Off Peak";
  }
  return null;
};

export const meterLabelFromDocument = (doc, options = {}) => {
  const meterId = Number(doc?.meterId ?? doc?.METER_ID);
  const code = String(doc?.meterCode ?? doc?.METER_CODE ?? "").trim();
  const setNo = Number(doc?.setNo ?? doc?.SET_NO);
  const isNetMeter = options.isNetMeter === true;
  const touBySet =
    options.isTou === true ||
    options.hasTouKwh === true ||
    (Number.isFinite(Number(options.kwhSetCount)) &&
      Number(options.kwhSetCount) >= 2);
  const direction = isNetMeter ? netMeterDirectionFromSet(setNo) : "";
  const band = peakOffPeakFromMeterCode(code);

  if (meterId === METER_ID_MDI) {
    return joinMeterLabel(direction, band, "MDI") || "MDI";
  }

  if (meterId === METER_ID_KVARH) {
    if (band) return joinMeterLabel(direction, band, "KVARH");
    if ((isNetMeter || touBySet) && Number.isFinite(setNo) && setNo > 0) {
      const setBand = setNo % 2 === 1 ? "Off Peak" : "Peak";
      return joinMeterLabel(direction, setBand, "KVARH");
    }
    return joinMeterLabel(direction, "KVARH") || "KVARH";
  }

  if (meterId === METER_ID_KWH) {
    if (isNetMeter && NET_METER_KWH_LABEL_BY_SET[setNo]) {
      return NET_METER_KWH_LABEL_BY_SET[setNo];
    }
    if (band) return joinMeterLabel(direction, "KWH", band);
    if ((isNetMeter || touBySet) && Number.isFinite(setNo) && setNo > 0) {
      const setBand = setNo % 2 === 1 ? "Off Peak" : "Peak";
      return joinMeterLabel(direction, "KWH", setBand);
    }
    return joinMeterLabel(direction, "KWH") || "KWH";
  }
  return doc?.meterCode || doc?.METER_CODE
    ? String(doc.meterCode || doc.METER_CODE)
    : "—";
};

/** Detect net-meter layout: KWH present in export sets (3/4) or 4 KWH sets. */
export const isNetMeterDocumentList = (documents = []) => {
  if (!Array.isArray(documents) || !documents.length) return false;
  const kwhSets = new Set();
  documents.forEach((doc) => {
    if (Number(doc?.meterId ?? doc?.METER_ID) !== METER_ID_KWH) return;
    const setNo = Number(doc?.setNo ?? doc?.SET_NO);
    if (Number.isFinite(setNo) && setNo > 0) kwhSets.add(setNo);
  });
  return kwhSets.has(3) || kwhSets.has(4) || kwhSets.size >= 4;
};

export const resolveIsNetMeter = (documents = [], options = {}) => {
  if (options.isNetMeter === true) return true;
  const phase = String(options.meterPhaseType || "").toLowerCase();
  if (phase.includes("net")) return true;
  return isNetMeterDocumentList(documents);
};

/**
 * MRC-only document URL normalizer.
 * Uses shared helper for PDF/JPEG; keeps PNG/GIF/WebP detection local
 * so other services are unaffected.
 */
export const normalizeImageSrc = (raw) => {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  const normalized = normalizeDocumentDataUrl(s);
  if (!normalized) return "";

  // If shared helper already set a definite type, keep it.
  if (
    normalized.startsWith("data:application/pdf") ||
    normalized.startsWith("data:image/")
  ) {
    const body = normalized.includes(",")
      ? normalized.slice(normalized.indexOf(",") + 1).replace(/\s/g, "")
      : normalized.replace(/\s/g, "");

    // Correct image MIME when payload was raw/wrongly typed (MRC only).
    if (/^iVBORw0KGgo/i.test(body)) {
      return `data:image/png;base64,${body}`;
    }
    if (/^R0lGOD/i.test(body)) {
      return `data:image/gif;base64,${body}`;
    }
    if (/^UklGR/i.test(body)) {
      return `data:image/webp;base64,${body}`;
    }
    if (/^\/9j\//i.test(body) && !normalized.startsWith("data:image/jpeg")) {
      return `data:image/jpeg;base64,${body}`;
    }
  }

  return normalized;
};

/** True when src can be shown in an <img> (skip PDFs / empty / junk). */
export const isDisplayableMeterImageSrc = (src) => {
  const s = String(src || "").trim();
  if (!s || s === "null" || s === "undefined") return false;
  if (isPdf(s)) return false;
  if (s.startsWith("data:application/pdf")) return false;
  if (/^https?:\/\//i.test(s)) return !/\.pdf(\?|#|$)/i.test(s);
  if (s.startsWith("data:image/")) {
    const body = s.includes(",")
      ? s.slice(s.indexOf(",") + 1).replace(/\s/g, "")
      : "";
    return body.length > 32;
  }
  return false;
};

export const isPdfMeterSrc = (src) => {
  const s = String(src || "").trim();
  if (!s || s === "null" || s === "undefined") return false;
  return isPdf(s);
};

const normalizeMrcTariffCode = (tariffCode) =>
  String(tariffCode ?? "").trim().padStart(2, "0");

export const findMrcTariffByCode = (tariffCode, tariffs = []) => {
  if (tariffCode == null || tariffCode === "") return null;
  const code = normalizeMrcTariffCode(tariffCode);
  const raw = String(tariffCode).trim();
  const rawNoDash = raw.replace(/-/g, "");
  return (
    tariffs.find((t) => {
      const apiCode = String(t.tariffCode ?? "").trim();
      const apiName = String(t.tariffName || "").trim();
      return (
        apiCode.padStart(2, "0") === code ||
        apiCode === raw ||
        apiCode.replace(/-/g, "") === rawNoDash ||
        apiName === raw ||
        apiName.replace(/-/g, "") === rawNoDash ||
        apiName.includes(`(${code})`) ||
        apiName.includes(`(${raw})`)
      );
    }) || null
  );
};

/** Match tariff code against getTariffsDetail → same desc as MRC table, e.g. B2b(12)T. */
export const resolveMrcTariffDisplay = (tariffValue, tariffs = []) => {
  const raw = String(tariffValue ?? "").trim();
  if (!raw) return "";
  const match = findMrcTariffByCode(raw, tariffs);
  if (!match) return raw;
  const name = String(match.tariffName || "").trim();
  const code = normalizeMrcTariffCode(match.tariffCode ?? raw);
  if (!name) return code || raw;
  if (name.includes(`(${code})`) || name.includes(`(${raw})`)) return name;
  return `${name}(${code || raw})`;
};

/** Bill TARIFF field: code only (e.g. 06). Name stays on TARIFF CATEGORY. */
export const resolveMrcTariffCode = (tariffValue, tariffs = []) => {
  const raw = String(tariffValue ?? "").trim();
  if (!raw) return "";
  const match = findMrcTariffByCode(raw, tariffs);
  const fromList = String(match?.tariffCode ?? "").trim();
  if (fromList) return normalizeMrcTariffCode(fromList);
  const paren = raw.match(/\(([^)]+)\)/);
  if (paren) return normalizeMrcTariffCode(paren[1]);
  if (/^\d{1,2}$/i.test(raw)) return normalizeMrcTariffCode(raw);
  return raw;
};

/** Same mapping as Change of Attributes / MRC form. */
const MRC_TARIFF_CATEGORY_FALLBACK = {
  "01": "Domestic",
  "02": "Commercial",
  "03": "Commercial",
  "04": "Industrial",
  "05": "Bulk Supply",
  "06": "Agricultural Tube Wells",
  "07": "Street Lighting",
  "08": "Residential Colonies",
};

/** CATEGORY on bill = tariff list consumerType (COA style). */
export const resolveMrcCategoryFromTariff = (tariffValue, tariffs = []) => {
  const match = findMrcTariffByCode(tariffValue, tariffs);
  const fromList = String(
    match?.consumerType ||
      match?.connectionType ||
      match?.category ||
      match?.connectionCategory ||
      ""
  ).trim();
  if (fromList) return fromList;
  const code = normalizeMrcTariffCode(tariffValue);
  return MRC_TARIFF_CATEGORY_FALLBACK[code] || "";
};

export const mapDocumentsToReadingRows = (documents = [], options = {}) => {
  if (!Array.isArray(documents)) return [];
  const isNetMeter = resolveIsNetMeter(documents, options);
  const kwhSetCount = (() => {
    const sets = new Set();
    documents.forEach((doc) => {
      if (Number(doc?.meterId ?? doc?.METER_ID) !== METER_ID_KWH) return;
      const setNo = Number(doc?.setNo ?? doc?.SET_NO);
      if (Number.isFinite(setNo) && setNo > 0) sets.add(setNo);
    });
    return sets.size;
  })();
  const isTou = !isNetMeter && kwhSetCount >= 2;
  return documents.map((doc, index) => {
    const claimed = toNumberOrNull(doc.consumerClaimedReading);
    const previous = toNumberOrNull(doc.previousReadCharged);
    const asCharged = toNumberOrNull(doc.presentReadCharged);
    const mf = toNumberOrNull(doc.mf);
    const unitsAdj = calcUnitsAdjusted(claimed, asCharged, mf);

    const unitsChargedRaw =
      doc.presentUnitsCharged != null && doc.presentUnitsCharged !== ""
        ? toNumberOrNull(doc.presentUnitsCharged)
        : calcUnitsCharged(asCharged, previous, mf);
    const unitsCharged =
      unitsChargedRaw != null ? Math.round(unitsChargedRaw) : null;

    let imageFlag =
      doc.consumerClaimedImageFlag ??
      doc.imageFlag ??
      doc.documentFlag ??
      doc.verificationFlag ??
      null;
    if (imageFlag === "" || imageFlag == null) {
      if (doc.consumerDialVerificationFlag === true) imageFlag = 1;
      else if (doc.consumerDialVerificationFlag === false) imageFlag = -1;
      else imageFlag = null;
    }

    // Prefer authorityApprovedReadings (same field we POST). Treat 0 as unset —
    // APIs often return 0 / authorityClaimedReading:0 as default and that would
    // hide the real approved reading if we prefer the wrong key first.
    const authorityRaw =
      doc.authorityApprovedReadings ??
      doc.AuthorityApprovedReadings ??
      doc.authorityApprovedReading ??
      doc.authorityClaimedReading ??
      doc.AuthorityClaimedReading ??
      doc.authorityReading;
    const authorityParsed = toNumberOrNull(authorityRaw);
    const authorityClaimed =
      authorityParsed === null || authorityParsed === 0
        ? null
        : authorityParsed;

    const siteSurveyRaw =
      doc.siteSurveyedReadings ??
        doc.siteSurveyReading ??
        doc.fieldSurveyReading ??
        doc.SiteSurveyReading;
    const siteSurveyParsed = toNumberOrNull(siteSurveyRaw);
    const siteSurveyReading =
      siteSurveyParsed === null || siteSurveyParsed === 0
        ? null
        : siteSurveyParsed;

    return {
      id: doc.Id ?? doc.id ?? `${doc.setNo}-${doc.meterId}-${index}`,
      setNo: doc.setNo ?? doc.SET_NO,
      meterId: doc.meterId ?? doc.METER_ID,
      tsNo: doc.tsNo ?? doc.TS_NO ?? 0,
      meterCode: doc.meterCode,
      meterLabel: meterLabelFromDocument(doc, {
        isNetMeter,
        isTou,
        kwhSetCount,
      }),
      meterSerialNo:
        doc.meterSerialNo ??
        doc.meterSerialNumber ??
        doc.METER_SERIAL_NO ??
        doc.serialNo ??
        "",
      mf: mf ?? toNumberOrNull(doc.MULTIPLY_FACTOR),
      meterRange: doc.meterRange,
      previous,
      asCharged,
      unitsCharged,
      claimed,
      authorityClaimed,
      siteSurveyReading,
      unitsAdj,
      unitsChargedRevised: calcUnitsChargedRevised(claimed, previous, mf),
      imageSrc: normalizeImageSrc(
        doc.consumerClaimedImage ||
          doc.ConsumerClaimedImage ||
          doc.claimedImage ||
          doc.claimedReadingImage ||
          doc.image ||
          ""
      ),
      siteSurveyImageSrc: normalizeImageSrc(
        doc.siteSurveyedImages ??
          doc.siteSurveyImage ??
          doc.fieldSurveyImage ??
          doc.SiteSurveyedImages ??
          ""
      ),
      imageFlag: imageFlag === "" || imageFlag == null ? null : Number(imageFlag),
      // Only the explicit API flag — do not infer from other fields
      isDocumentReuploaded: parseMrcIsDocumentReuploaded(
        doc.isDocumentReuploaded ?? doc.IsDocumentReuploaded
      ),
      consumerDialVerificationRemarks: String(
        doc.consumerDialVerificationRemarks ?? ""
      ).trim(),
      imageRemarks:
        doc.consumerDialVerificationRemarks ||
        doc.consumerClaimedImageRemarks ||
        doc.imageRemarks ||
        "",
    };
  });
};

/** CP52 meter rows — shared by prepare popup HTML and PDF. */
export const getCp52MeterReadingRows = (documents = [], formData = {}) => {
  const rows = mapDocumentsToReadingRows(documents);
  const withReading = rows.filter(
    (row) =>
      row.authorityClaimed != null ||
      row.claimed != null ||
      row.siteSurveyReading != null ||
      row.asCharged != null
  );
  if (withReading.length) return withReading;
  if (rows.length) return rows;
  if (!formData || typeof formData !== "object") return [];
  return [
    {
      id: "fallback",
      setNo: formData.setNo,
      meterId: METER_ID_KWH,
      meterLabel: "",
      meterSerialNo: formData.meterNo,
      asCharged: formData.presentAsCharged,
      authorityClaimed: formData.presentToBeCharged,
      previous: null,
      mf: 1,
    },
  ];
};

export const resolveCp52MeterToBeReading = (
  row,
  formData = {},
  { allowFormFallback = false } = {}
) => {
  if (isMdiMeterRow(row)) return null;
  if (hasMrcPresentReading(row.authorityClaimed)) {
    return toNumberOrNull(row.authorityClaimed);
  }
  if (hasMrcPresentReading(row.siteSurveyReading)) {
    return toNumberOrNull(row.siteSurveyReading);
  }
  if (hasMrcPresentReading(row.claimed)) {
    return toNumberOrNull(row.claimed);
  }
  if (allowFormFallback && hasMrcPresentReading(formData.presentToBeCharged)) {
    return toNumberOrNull(formData.presentToBeCharged);
  }
  return null;
};

export const resolveCp52MeterUnitsAdjusted = (
  row,
  formData = {},
  { allowFormFallback = false } = {}
) => {
  if (isMdiMeterRow(row)) return null;
  const toBe = resolveCp52MeterToBeReading(row, formData);
  // No present/to-be reading → do not show Unit Adjusted
  if (!hasMrcPresentReading(toBe)) return null;
  // Units Adj. = (To-Be reading − As Charged) × MF
  const calculated = calcUnitsAdjusted(toBe, row.asCharged, row.mf);
  if (calculated != null) return calculated;
  if (allowFormFallback) {
    return toNumberOrNull(formData.unitAdjusted);
  }
  return toNumberOrNull(row.unitsAdj);
};

/** Merge first KWH/document row into CP52 base data for PDF fallback fields. */
export const enrichCp52BaseDataFromDocuments = (baseData = {}, documents = []) => {
  const rows = getCp52MeterReadingRows(documents, baseData);
  const primary =
    rows.find(
      (row) => Number(row.meterId) === METER_ID_KWH && !isMdiMeterRow(row)
    ) || rows[0];
  if (!primary) return baseData;
  const toBe = resolveCp52MeterToBeReading(primary, baseData);
  return {
    ...baseData,
    meterNo: primary.meterSerialNo || baseData.meterNo || "",
    setNo: primary.setNo ?? baseData.setNo ?? "",
    previousReading: primary.previous ?? baseData.previousReading ?? "",
    presentAsCharged: primary.asCharged ?? baseData.presentAsCharged ?? "",
    presentToBeCharged: toBe ?? baseData.presentToBeCharged ?? "",
    unitCharged:
      calcUnitsCharged(primary.asCharged, primary.previous, primary.mf) ??
      baseData.unitCharged ??
      "",
  };
};

const cp52MeterCell = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return formatDisplayValue(value) === "—" ? "" : formatDisplayValue(value);
};

/** Meter table rows for CP52 PDF (matches prepare popup logic). */
export const buildCp52MeterPdfRows = (documents = [], formData = {}) => {
  const rows = getCp52MeterReadingRows(documents, formData);
  let formUnitAdjUsed = false;

  return rows.map((row, index) => {
    const meterId = Number(row.meterId ?? 0);
    const isMdi = isMdiMeterRow(row);
    const rowFormData = index === 0 ? formData : {};
    const toBe = resolveCp52MeterToBeReading(row, rowFormData);
    let unitsAdj = resolveCp52MeterUnitsAdjusted(row, formData, {
      allowFormFallback: !formUnitAdjUsed,
    });
    if (!isMdi && unitsAdj != null && !formUnitAdjUsed) {
      const usedFormFallback =
        calcUnitsAdjusted(toBe, row.asCharged, row.mf) == null &&
        toNumberOrNull(formData.unitAdjusted) === unitsAdj;
      if (usedFormFallback) formUnitAdjUsed = true;
    }
    const unitsCharged =
      calcUnitsCharged(row.asCharged, row.previous, row.mf) ??
      (index === 0 ? toNumberOrNull(formData.unitCharged) : null);

    return {
      meterIdForGen:
        meterId === METER_ID_KWH || meterId === METER_ID_KVARH
          ? String(meterId)
          : "",
      meterIdForMdi: meterId === METER_ID_MDI ? String(meterId) : "",
      setNo: cp52MeterCell(row.setNo) || (index === 0 ? cp52MeterCell(formData.setNo) : ""),
      meterNo:
        cp52MeterCell(row.meterSerialNo) ||
        (index === 0 ? cp52MeterCell(formData.meterNo) : ""),
      presentAsCharged:
        cp52MeterCell(row.asCharged) ||
        (index === 0 ? cp52MeterCell(formData.presentAsCharged) : ""),
      presentToBeCharged: isMdi
        ? ""
        : hasMrcPresentReading(toBe)
          ? cp52MeterCell(toBe)
          : "",
      unitAdjusted: isMdi
        ? ""
        : hasMrcPresentReading(toBe) && unitsAdj != null && unitsAdj !== ""
          ? formatUnitsAdjDisplay(unitsAdj, "")
          : "",
      unitCharged: (() => {
        const v =
          unitsCharged ??
          (index === 0 ? toNumberOrNull(formData.unitCharged) : null);
        if (v === null || v === undefined || v === "") return "";
        return formatUnitsChargedDisplay(v, "");
      })(),
    };
  });
};

/** Rows that require Accept/Reject on claimed reading images (non-MDI with image). */
export const getMrcImageVerificationRows = (documents = []) =>
  mapDocumentsToReadingRows(documents).filter(
    (row) => !isMdiMeterRow(row) && Boolean(row.imageSrc)
  );

export const getMrcImageVerificationState = (documents = []) => {
  const rows = getMrcImageVerificationRows(documents);
  const pending = rows.filter(
    (row) => row.imageFlag !== 1 && row.imageFlag !== -1
  );
  const rejected = rows.filter((row) => row.imageFlag === -1);
  const allActed = pending.length === 0;
  const hasReject = rejected.length > 0;
  const allAccepted = allActed && !hasReject;
  return { rows, pending, rejected, allActed, hasReject, allAccepted };
};

// export const MRC_DOCUMENT_VERIFIED_REMARK = "DOCUMENT VERIFIED";
export const MRC_DOCUMENT_VERIFIED_REMARK = "Document Verified";

export const MRC_DOCUMENT_REUPLOADED_REMARK = "Document Reuploaded";

/**
 * Evidence popup remarks:
 * - Accepted (flag 1) → verified remarks (not "Reuploaded")
 * - Rejected (flag -1) → reject remarks (not "Reuploaded")
 * - Pending + isDocumentReuploaded → "Document Reuploaded"
 * - otherwise → consumerDialVerificationRemarks (empty → hide)
 */
export const getMrcEvidenceRemarksLabel = (row = {}) => {
  const flag = Number(row.imageFlag);
  const dial = String(
    row.consumerDialVerificationRemarks ?? row.imageRemarks ?? ""
  ).trim();
  const isReuploadDial = /^documents?\s+re-?uploaded$/i.test(dial);

  if (flag === 1) {
    return dial && !isReuploadDial ? dial : MRC_DOCUMENT_VERIFIED_REMARK;
  }
  if (flag === -1) {
    return isReuploadDial ? "" : dial;
  }
  if (parseMrcIsDocumentReuploaded(row.isDocumentReuploaded)) {
    return MRC_DOCUMENT_REUPLOADED_REMARK;
  }
  if (isReuploadDial) return "";
  return dial;
};

/** Variant for remarks chip color in the evidence popup. */
export const getMrcEvidenceRemarksVariant = (row = {}) => {
  const flag = Number(row.imageFlag);
  if (flag === 1) return "verified";
  if (flag === -1) return "rejected";
  if (parseMrcIsDocumentReuploaded(row.isDocumentReuploaded)) {
    return "reuploaded";
  }
  const label = getMrcEvidenceRemarksLabel(row);
  if (!label) return "";
  if (/verified/i.test(label)) return "verified";
  return "rejected";
};

/** History / reupload-page context for evidence remarks. */
export const applyMrcEvidenceRemarksMode = (row = {}, mode = "auto") => {
  if (!row) return row;
  if (mode === "reupload") {
    return { ...row, isDocumentReuploaded: true, imageFlag: null };
  }
  if (mode === "none") {
    return {
      ...row,
      isDocumentReuploaded: false,
      imageFlag: null,
      consumerDialVerificationRemarks: "",
      imageRemarks: "",
    };
  }
  if (mode === "verified") {
    return {
      ...row,
      isDocumentReuploaded: false,
      imageFlag: 1,
      consumerDialVerificationRemarks: MRC_DOCUMENT_VERIFIED_REMARK,
      imageRemarks: MRC_DOCUMENT_VERIFIED_REMARK,
    };
  }
  if (mode === "rejected") {
    const dial = String(
      row.consumerDialVerificationRemarks ?? row.imageRemarks ?? ""
    ).trim();
    const remarks =
      dial &&
      !/^documents?\s+re-?uploaded$/i.test(dial) &&
      !/document verified/i.test(dial)
        ? dial
        : "Documents rejected";
    return {
      ...row,
      isDocumentReuploaded: false,
      imageFlag: -1,
      consumerDialVerificationRemarks: remarks,
      imageRemarks: remarks,
    };
  }
  // Document Verification history: per-doc accept/reject wins; ignore reupload flag.
  if (mode === "verify-history-accept" || mode === "verify-history-reject") {
    const flag = Number(row.imageFlag);
    if (flag === 1 || flag === -1) {
      return { ...row, isDocumentReuploaded: false };
    }
    return applyMrcEvidenceRemarksMode(
      row,
      mode === "verify-history-reject" ? "rejected" : "verified"
    );
  }
  return row;
};

export const buildMrcVerifyMeterData = (documents = []) =>
  getMrcImageVerificationRows(documents).map((row) => {
    const accepted = row.imageFlag === 1;
    return {
      meterId: Number(row.meterId) || 0,
      setNo: Number(row.setNo) || 0,
      tsNo: Number(row.tsNo) || 0,
      consumerDialVerificationRemarks: accepted
        ? MRC_DOCUMENT_VERIFIED_REMARK
        : String(row.imageRemarks || "").trim() || "Reading does not matched.",
      consumerDialVerificationFlag: accepted,
    };
  });

export const buildMrcImageObjectionRemarks = (documents = []) => {
  const { rejected } = getMrcImageVerificationState(documents);
  if (!rejected.length) return "Documents rejected";
  const labels = rejected.map(
    (row) => `${row.meterLabel} (Set ${row.setNo})`
  );
  return `Documents rejected: ${labels.join(", ")}.`;
};

/** meterData payload for mrc/authorityApproval / forwardForBilling.
 * Only rows with an Authority Approved Reading are included.
 * unitsAdjusted = (authorityApproved − asCharged) × MF
 */
export const buildMrcAuthorityApprovalMeterData = (
  documents = [],
  authorityReadingsById = {}
) =>
  mapDocumentsToReadingRows(documents)
    .map((row) => {
      const fromInput = authorityReadingsById[row.id];
      const raw =
        fromInput !== undefined && String(fromInput).trim() !== ""
          ? String(fromInput).trim()
          : row.authorityClaimed != null
            ? String(row.authorityClaimed)
            : "";
      // Support claimed:N / survey:N select values as well as plain numbers.
      const cleaned = raw.includes(":")
        ? raw.slice(raw.indexOf(":") + 1).trim()
        : raw;
      const reading = toNumberOrNull(cleaned);
      if (reading === null || reading === 0) return null;
      const unitsAdjusted = calcUnitsAdjusted(reading, row.asCharged, row.mf);
      return {
        meterId: Number(row.meterId) || 0,
        setNo: Number(row.setNo) || 0,
        tsNo: Number(row.tsNo) || 0,
        authorityApprovedReadings: reading,
        unitsAdjusted: unitsAdjusted === null ? 0 : unitsAdjusted,
      };
    })
    .filter(Boolean);

/** meterData payload for Site Survey (mrc/siteSurvey).
 * Only rows where Site Survey Reading was entered are included.
 * unitsAdjusted = (siteSurvey − asCharged) × MF
 */
export const buildMrcSiteSurveyMeterData = (
  documents = [],
  siteSurveyReadingsById = {},
  siteSurveyImagesById = {}
) =>
  mapDocumentsToReadingRows(documents)
    .map((row) => {
      const fromInput = siteSurveyReadingsById[row.id];
      const raw =
        fromInput !== undefined && String(fromInput).trim() !== ""
          ? String(fromInput).trim()
          : row.siteSurveyReading != null
            ? String(row.siteSurveyReading)
            : "";
      const reading = toNumberOrNull(raw);
      if (reading === null || reading === 0) return null;
      const image =
        siteSurveyImagesById[row.id] ||
        row.siteSurveyImageSrc ||
        "";
      const unitsAdjusted = calcUnitsAdjusted(reading, row.asCharged, row.mf);
      return {
        meterId: Number(row.meterId) || 0,
        setNo: Number(row.setNo) || 0,
        tsNo: Number(row.tsNo) || 0,
        siteSurveyedReadings: reading,
        siteSurveyedImages: image || "",
        unitsAdjusted: unitsAdjusted === null ? 0 : unitsAdjusted,
      };
    })
    .filter(Boolean);

const mrcMeterRowKey = (row = {}) =>
  `${Number(row.setNo ?? row.SET_NO ?? 0)}|${Number(
    row.meterId ?? row.METER_ID ?? 0
  )}|${Number(row.tsNo ?? row.TS_NO ?? 0)}`;

const parseJsonIfString = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const isMrcHistoryMetaRow = (row) =>
  Boolean(
    row &&
      typeof row === "object" &&
      (row.historyProgressId != null ||
        row.historyId != null ||
        row.lastModifiedBy != null ||
        row.lastModificationDate != null) &&
      row.meterId == null &&
      row.METER_ID == null &&
      row.setNo == null &&
      row.SET_NO == null
  );

const looksLikeMrcMeterRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  if (isMrcHistoryMetaRow(row)) return false;
  if (
    row.siteSurveyedReadings != null ||
    row.siteSurveyReading != null ||
    row.authorityApprovedReadings != null ||
    row.authorityApprovedReading != null
  ) {
    return true;
  }
  if (row.meterId == null && row.METER_ID == null) return false;
  if (row.setNo == null && row.SET_NO == null) return false;
  return (
    row.siteSurveyedImages != null ||
    row.siteSurveyImage != null ||
    row.unitsAdjusted != null ||
    row.consumerClaimedReading != null
  );
};

const looksLikeFullMrcDocumentRow = (row) =>
  Boolean(
    row &&
      (row.consumerClaimedReading != null ||
        row.consumerClaimedImage != null ||
        row.presentReadCharged != null ||
        row.previousReadCharged != null)
  );

const coerceMrcMeterRows = (raw) => {
  const parsed = parseJsonIfString(raw) ?? raw;
  if (!parsed) return [];
  if (Array.isArray(parsed)) {
    return parsed.filter(looksLikeMrcMeterRow);
  }
  if (typeof parsed === "object") {
    if (Array.isArray(parsed.meterData)) return coerceMrcMeterRows(parsed.meterData);
    if (Array.isArray(parsed.documents)) return coerceMrcMeterRows(parsed.documents);
    if (Array.isArray(parsed.mrcDocuments)) {
      return coerceMrcMeterRows(parsed.mrcDocuments);
    }
    if (looksLikeMrcMeterRow(parsed)) return [parsed];
  }
  return [];
};

export const historyMeterHasSiteSurvey = (meterRows = []) =>
  (Array.isArray(meterRows) ? meterRows : []).some((row) => {
    const raw =
      row?.siteSurveyedReadings ??
      row?.siteSurveyReading ??
      row?.fieldSurveyReading;
    const parsed = toNumberOrNull(raw);
    return parsed != null && parsed !== 0;
  });

/** Meter rows stored on a history detail (site survey / authority snapshot). */
export const extractMrcHistoryMeterSnapshot = (detail = {}, extraData) => {
  if (!detail || typeof detail !== "object") {
    return coerceMrcMeterRows(extraData);
  }
  const nested = [
    detail.meterData,
    detail.MeterData,
    detail.mrcMeterData,
    detail.documents,
    detail.mrcDocuments,
    detail.readingDocuments,
  ];
  for (const candidate of nested) {
    const rows = coerceMrcMeterRows(candidate);
    if (rows.length) return rows;
  }
  const extraRows = Array.isArray(extraData)
    ? extraData.filter((row) => !isMrcHistoryMetaRow(row))
    : extraData;
  const fromExtra = coerceMrcMeterRows(extraRows);
  if (fromExtra.length) return fromExtra;
  if (looksLikeMrcMeterRow(detail) && !isMrcHistoryMetaRow(detail)) {
    return [detail];
  }
  return [];
};

export const overlayMrcHistoryMeterOnDocuments = (
  documents = [],
  meterRows = []
) => {
  const docs = Array.isArray(documents) ? documents : [];
  if (!docs.length || !Array.isArray(meterRows) || !meterRows.length) {
    return docs;
  }

  const byKey = new Map();
  meterRows.forEach((row) => {
    byKey.set(mrcMeterRowKey(row), row);
  });

  return docs.map((doc) => {
    const exactKey = mrcMeterRowKey(doc);
    let snap = byKey.get(exactKey);
    if (!snap) {
      const loose = meterRows.filter(
        (row) =>
          Number(row.setNo ?? row.SET_NO ?? 0) ===
            Number(doc.setNo ?? doc.SET_NO ?? 0) &&
          Number(row.meterId ?? row.METER_ID ?? 0) ===
            Number(doc.meterId ?? doc.METER_ID ?? 0)
      );
      if (loose.length === 1) snap = loose[0];
    }
    if (!snap) return doc;

    const next = { ...doc };
    const surveyRaw =
      snap.siteSurveyedReadings ??
      snap.siteSurveyReading ??
      snap.fieldSurveyReading;
    if (surveyRaw !== undefined) {
      next.siteSurveyedReadings = surveyRaw;
      next.siteSurveyReading = surveyRaw;
    }

    const surveyImg =
      snap.siteSurveyedImages ??
      snap.siteSurveyImage ??
      snap.fieldSurveyImage;
    if (surveyImg !== undefined && String(surveyImg).trim() !== "") {
      next.siteSurveyedImages = surveyImg;
      next.siteSurveyImage = surveyImg;
    }

    const authRaw =
      snap.authorityApprovedReadings ??
      snap.authorityApprovedReading ??
      snap.authorityClaimedReading;
    if (authRaw !== undefined) {
      next.authorityApprovedReadings = authRaw;
      next.authorityApprovedReading = authRaw;
    }

    if (snap.unitsAdjusted !== undefined) {
      next.unitsAdjusted = snap.unitsAdjusted;
    }
    return next;
  });
};

/**
 * Activity-log history must not use live getMRCDocuments survey readings
 * (a later site survey overwrites those). Prefer the history snapshot.
 */
export const resolveMrcHistoryReadingDocuments = ({
  liveDocuments = [],
  historyMeterData = [],
  priorSurveyMeterData = [],
} = {}) => {
  const live = Array.isArray(liveDocuments) ? liveDocuments : [];
  const historyRows = Array.isArray(historyMeterData) ? historyMeterData : [];
  const priorRows = Array.isArray(priorSurveyMeterData)
    ? priorSurveyMeterData
    : [];
  const base =
    historyRows.length && historyRows.some(looksLikeFullMrcDocumentRow)
      ? historyRows
      : live;
  return overlayMrcHistoryMeterOnDocuments(
    overlayMrcHistoryMeterOnDocuments(base, historyRows),
    priorRows
  );
};
